import type { User } from '@/types/user'

/** The error message of a sign-in that navigated away to finish on the provider's pages. */
export const REDIRECT_IN_PROGRESS = 'REDIRECT_IN_PROGRESS'

/** Who an identity provider vouched for, and through which one (`google`, `microsoft`) when that is known. */
export interface SsoIdentity {
  email: string | null
  identityProvider?: string
}

/** Who is signed in, read without a network round trip (route guards, the 401 retry). */
export interface AuthSession {
  uid: string
  email: string | null
  emailVerified: boolean
}

export interface TotpEnrollmentInfo {
  secret: string
  qrCodeUrl: string
  secretKey: string
}

/** A second factor enrolled on the account, as offered during a challenge. */
export interface MfaFactor {
  uid: string
  factorId: string
  displayName?: string | null
}

/** A sign-in halted for its second factor; only the provider that raised it can resolve it. */
export interface MfaChallenge {
  readonly hints: readonly MfaFactor[]
}

/** The second-factor steps the app prompts for itself. */
export interface MfaProvider {
  isMfaEnabled(): Promise<boolean>
  getEnrolledFactors(): Promise<MfaFactor[]>
  startTotpEnrollment(): Promise<TotpEnrollmentInfo>
  completeTotpEnrollment(verificationCode: string): Promise<void>
  clearPendingEnrollment(): void
  unenrollAllFactors(): Promise<void>
  isMfaRequiredError(error: unknown): boolean
  /** The challenge carried by an error {@link isMfaRequiredError} accepted. */
  getMfaResolver(error: unknown): MfaChallenge
  /** Who passed the first factor of an error {@link isMfaRequiredError} accepted: there is no user yet. */
  getMfaRequiredIdentity(error: unknown): SsoIdentity
  handleTotpChallenge(challenge: MfaChallenge, factorIndex: number, verificationCode: string): Promise<void>
  isRecentLoginRequiredError(error: unknown): boolean
  reauthenticateWithPassword(password: string): Promise<void>
  reauthenticateWithGoogle(): Promise<void>
  reauthenticateWithMicrosoft(): Promise<void>
  /** `'password'`, `'google.com'`, `'microsoft.com'`, or `null` when unknown. */
  getCurrentAuthProvider(): string | null
}

/**
 * Where a person signs in. Everything in the app that signs someone in or out,
 * or needs the bearer token the API accepts, goes through this — never through
 * a provider's SDK — so an organisation can be moved to another provider
 * without the rest of the app noticing (ADR-045 §7).
 */
export interface AuthProvider {
  signIn(email: string, password: string): Promise<User>
  signUp(email: string, password: string): Promise<User>
  /** `loginHint` is for a provider that redirects; a provider that opens its own picker ignores it. */
  signInWithSSO(provider: string, loginHint?: string): Promise<User>
  signOut(): Promise<void>
  getCurrentUser(): Promise<User | null>
  onAuthStateChanged(callback: (user: User | null) => void): () => void
  handleRedirectResult(): Promise<User | null>
  currentSession(): AuthSession | null
  /** The API's bearer token, or `null`; `forceRefresh` picks up claims the backend just wrote. */
  getIdToken(forceRefresh?: boolean): Promise<string | null>
  /** The claims of the current token, or `null` when nobody is signed in. */
  getTokenClaims(forceRefresh?: boolean): Promise<Record<string, unknown> | null>
  /** Whether `error` means the session has ended and no refresh can recover it. */
  isSessionExpiredError(error: unknown): boolean
  sendEmailVerification(): Promise<void>
  /** Admin impersonation: sign in with a token the backend minted. */
  signInWithCustomToken(token: string): Promise<void>
  /** Client portal: whether `url` is a passwordless sign-in link. */
  isSignInLink(url: string): boolean
  signInWithEmailLink(email: string, url: string): Promise<void>
  readonly mfa: MfaProvider
}
